import { motion, useAnimation } from 'framer-motion';
import { useEffect, useState } from 'react';
import styles from './playerIcon.module.css';
import { PlayerIconProps, PlayerRisk } from './playerIcon.types';
import warning from '../../assets/warning.png';
import { computeTimeBadgeDisplay } from '../../utils/helper';
import { EventType } from '@inplay/models';

const PlayerIcon = (props: PlayerIconProps) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const pulseControls = useAnimation();

  useEffect(() => {
    const baseShadow =
      props.player.risk === PlayerRisk.LOW
        ? '0px 0px 10px rgba(0, 247, 19, 0.133)'
        : props.player.risk === PlayerRisk.INTERVENTION_OFFERED
          ? '0px 0px 10px rgba(143, 247, 0, 0.135)'
          : props.player.risk === PlayerRisk.INTERVENTION_REQUIRED
            ? '0px 0px 10px rgba(255, 127, 0, 0.198)'
            : '0px 0px 10px rgba(255, 0, 0, 0.133)';
    pulseControls.set({ boxShadow: baseShadow });
  }, [props.player.risk, pulseControls]);

  const handleHoverStart = () => {
    setIsHovered(true);
    props.onHoverChange?.(true);

    const colors =
      props.player.risk === PlayerRisk.LOW
        ? [
            '0px 0px 10px rgba(0, 247, 19, 0.133)',
            '0px 0px 20px rgba(0, 247, 19, 0.533)',
            '0px 0px 10px rgba(0, 247, 19, 0.133)',
          ]
        : props.player.risk === PlayerRisk.INTERVENTION_OFFERED
          ? [
              '0px 0px 10px rgba(143, 247, 0, 0.135)',
              '0px 0px 20px rgba(143, 247, 0, 0.635)',
              '0px 0px 10px rgba(143, 247, 0, 0.135)',
            ]
          : props.player.risk === PlayerRisk.INTERVENTION_REQUIRED
            ? [
                '0px 0px 10px rgba(255, 127, 0, 0.198)',
                '0px 0px 20px rgba(255, 127, 0, 0.698)',
                '0px 0px 10px rgba(255, 127, 0, 0.198)',
              ]
            : [
                '0px 0px 10px rgba(255, 0, 0, 0.133)',
                '0px 0px 20px rgba(255, 0, 0, 0.533)',
                '0px 0px 10px rgba(255, 0, 0, 0.133)',
              ];

    pulseControls.start({
      boxShadow: colors,
      transition: {
        duration: 1,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut',
      },
    });
  };

  const handleHoverEnd = () => {
    setIsHovered(false);
    props.onHoverChange?.(false);
    pulseControls.stop();

    const baseShadow =
      props.player.risk === PlayerRisk.LOW
        ? '0px 0px 10px rgba(0, 247, 19, 0.133)'
        : props.player.risk === PlayerRisk.INTERVENTION_OFFERED
          ? '0px 0px 10px rgba(143, 247, 0, 0.135)'
          : props.player.risk === PlayerRisk.INTERVENTION_REQUIRED
            ? '0px 0px 10px rgba(255, 127, 0, 0.198)'
            : '0px 0px 10px rgba(255, 0, 0, 0.133)';
    pulseControls.set({ boxShadow: baseShadow });
  };

  const showWarning: boolean =
    Boolean(props.deadline) || Boolean(props.player.isExcluded);

  const timeBadgeDisplay: string | null = computeTimeBadgeDisplay(
    props.deadline,
    props.currentMinute,
  );

  const timeBadgeClassName: string | null = props.deadline
    ? props.deadline.deadlineType === EventType.INTERVENTION
      ? styles.timeBadgeOrangeOutline
      : styles.timeBadgeYellowOutline
    : null;

  return (
    <motion.div
      className={`${styles.container} ${
        props.player.risk === PlayerRisk.LOW
          ? styles.low
          : props.player.risk === PlayerRisk.INTERVENTION_OFFERED
            ? styles.intervention_offered
            : props.player.risk === PlayerRisk.INTERVENTION_REQUIRED
              ? styles.intervention_required
              : styles.excluded
      }`}
      onHoverStart={() => {
        if (props.noAnimation) return;
        handleHoverStart();
      }}
      onHoverEnd={() => {
        if (props.noAnimation) return;
        handleHoverEnd();
      }}
      animate={{ scale: isHovered ? 1.2 : 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{ position: 'relative' }}
      onClick={() => props.onClick()}
    >
      <motion.div
        animate={pulseControls}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      />

      <div className={styles.gradient}>
        {timeBadgeDisplay && (
          <div className={`${styles.timeBadge} ${timeBadgeClassName}`}>
            {timeBadgeDisplay}
          </div>
        )}
        {showWarning && (
          <div className={styles.warningBadge}>
            <img
              src={warning}
              alt="Attention required"
              className={styles.warningIcon}
            />
          </div>
        )}
        <div className={styles.playerImgContainer}>
          <img
            src={props.player.faceImage ?? props.player.image}
            className={styles.playerImg}
            alt=""
          />
        </div>
      </div>
    </motion.div>
  );
};

export default PlayerIcon;
